import React, { useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { MediabilityLogo } from "@/components/Logo";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("david@mediability.com");
  const [password, setPassword] = useState("demo1234");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  // embed=1: the proposal page shows the product inside a full-screen frame and always starts on sign-in
  const embed = params.get("embed") === "1";
  const home = loc.state?.from?.pathname || "/overview";

  if (!loading && user && !embed) return <Navigate to={home} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(home, { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-2">
      {/* Brand panel: image + logo only */}
      <section className="relative hidden overflow-hidden bg-[#0a0a0a] lg:block" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1601506521937-0121a7fc2a6b?auto=format&fit=crop&w=1800&q=80"
          alt=""
          className="absolute inset-0 h-full w-full select-none object-cover object-[35%_center] fade-in"
          style={{ filter: "saturate(0.82) contrast(1.04)" }}
          draggable={false}
          data-testid="login-brand-image"
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-black/55 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/35 to-transparent" />
        <div className="absolute left-12 top-10 xl:left-16">
          <MediabilityLogo variant="white" className="h-8" testId="login-logo" />
        </div>
      </section>

      {/* Sign-in panel */}
      <section className="flex flex-col px-6 py-8 sm:px-12 lg:px-20 xl:px-28">
        <div className="lg:hidden">
          <MediabilityLogo className="h-7" testId="login-logo-mobile" />
        </div>

        <div className="flex flex-1 items-center justify-center">
          <form onSubmit={submit} className="w-full max-w-[380px]" data-testid="login-form" noValidate>
            <div className="fade-up">
              <h2 className="text-[30px] font-semibold leading-tight tracking-[-0.03em]">Sign in</h2>
              <p className="mt-2 text-[15px] text-muted-foreground">Use your Mediability account.</p>
            </div>

            <div className="fade-up d-1 mt-9 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[13px] font-medium">Work e-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-xl px-3.5 text-[15px]"
                  data-testid="login-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[13px] font-medium">Password</Label>
                  <button
                    type="button"
                    onClick={() => toast("Password resets are handled by your administrator.")}
                    className="text-[12.5px] text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
                    data-testid="login-forgot-password-button"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-xl px-3.5 pr-11 text-[15px]"
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="login-toggle-password-button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <Button type="submit" size="lg" disabled={busy} className="fade-up d-2 mt-8 w-full gap-2" data-testid="login-submit-button">
              {busy ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> Signing in
                </>
              ) : (
                <>
                  Sign in <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            <p className="fade-up d-3 mt-8 text-[12.5px] leading-relaxed text-muted-foreground">
              Access is provided by Mediability. If you need an account, contact your administrator.
            </p>
          </form>
        </div>

        <div className="flex items-center justify-between text-[12px] text-muted-foreground">
          <span>© {new Date().getFullYear()} Mediability</span>
          <span className="lg:hidden">Content | Engineered</span>
        </div>
      </section>
    </div>
  );
}
